import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearError } from '../store/slices/authSlice';
import brandImage from '../assets/images/khizly.png';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error, isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearError());
    dispatch(login(formData));
  };
  return (
    <Grid container sx={{ minHeight: '100vh' }}>
      <Grid
        item
        xs={12}
        md={8}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          position: 'relative',
          overflow: 'hidden',
          minHeight: { xs: '40vh', md: '100vh' },
        }}
      >
        <Box
          component="div"
          sx={{
            flexGrow: 1,
            backgroundImage: `url(${brandImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.9)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(120deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 60%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: { md: '50%', lg: '45%' },
            left: { md: '10%', lg: '12%' },
            transform: 'translateY(-50%)',
            color: 'white',
            maxWidth: '420px',
          }}
        >
          <Typography variant={isSmallScreen ? 'h4' : 'h3'} fontWeight={700} gutterBottom>
            Khizly Commerce
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, lineHeight: 1.6 }}>
            Delivering premium shopping experiences with intelligent insights, modern analytics,
            and effortless management for your entire catalog.
          </Typography>
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: (theme) => theme.palette.background.default,
          px: { xs: 3, sm: 6 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Paper
          elevation={6}
          sx={{
            width: '100%',
            maxWidth: 420,
            p: { xs: 4, md: 5 },
            borderRadius: 4,
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)',
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography component="h1" variant="h4" fontWeight={700} gutterBottom>
              Welcome back
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to access the Khizly admin dashboard.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 2,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>

          <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              Need help? Contact <Box component="span" fontWeight={600}>khizlystore@gmail.com</Box>
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Login;